import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle, BadgeCheck, Boxes, Bug, Camera, ClipboardList, Database,
  FileWarning, Languages, Loader2, LogOut, Radio, ScanLine, Search,
  ShieldCheck, Sparkles, Stethoscope, Upload, Users, BarChart3, BookOpen, TrendingUp, Wrench,
  MessageSquareQuote, PackageCheck, CalendarRange,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import PortalLogo from "../components/admin/PortalLogo";
import "../styles/clinical-portals.css";
import { adminApi, useAdminAuth } from "../lib/adminAuth";
import FoundingFunnelWidget from "../components/FoundingFunnelWidget";
import MedicationRequestsWidget from "../components/MedicationRequestsWidget";
import CoverageBacklogWidget from "../components/CoverageBacklogWidget";

export default function AdminDashboard() {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [week1, setWeek1] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await adminApi().get("/admin/dashboard/metrics");
        if (!cancelled) setMetrics(data);
      } catch (e) {
        if (cancelled) return;
        const msg = e?.response?.data?.detail || e.message;
        setError(msg);
        toast.error(`Failed to load metrics: ${msg}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  function onLogout() {
    logout();
    navigate("/admin", { replace: true });
  }

  // Fires the /admin/v2/seo/ping-search-engines endpoint and surfaces the
  // per-engine outcome as a single toast. Skipped/deprecated states are
  // shown explicitly so admins know IndexNow is configured (or isn't).
  const [pinging, setPinging] = useState(false);
  const [lastPing, setLastPing] = useState(null);

  // Hydrate the "Last pinged …" subtitle on mount.
  useEffect(() => {
    let cancelled = false;
    adminApi().get("/admin/v2/seo/ping-search-engines/last")
      .then(({ data }) => { if (!cancelled) setLastPing(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // GSC + IndexNow setup state for the qa-gsc-setup card subtitle.
  const [gscStatus, setGscStatus] = useState(null);
  useEffect(() => {
    let cancelled = false;
    adminApi().get("/admin/v2/seo/gsc-status")
      .then(({ data }) => { if (!cancelled) setGscStatus(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Pair-content generator status — counts AI-generated vs pending pairs.
  const [pairStatus, setPairStatus] = useState(null);
  useEffect(() => {
    let cancelled = false;
    adminApi().get("/admin/v2/pair-content/candidates")
      .then(({ data }) => {
        if (cancelled) return;
        const total = data?.length || 0;
        const inDb = (data || []).filter((p) => p.in_db).length;
        setPairStatus({ total, generated: inDb, remaining: total - inDb });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Backend error log — 24-hour rolling summary for the dashboard tile.
  const [errorLog, setErrorLog] = useState(null);
  useEffect(() => {
    let cancelled = false;
    adminApi().get("/admin/v2/error-log/summary?days=1")
      .then(({ data }) => { if (!cancelled) setErrorLog(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Week 1 launch progress — drives the Isotretinoin banner.
  useEffect(() => {
    let cancelled = false;
    adminApi().get("/admin/v2/verification/week-plan")
      .then(({ data }) => { if (!cancelled) setWeek1(data?.week1 || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  async function onPingSearchEngines() {
    if (pinging) return;
    setPinging(true);
    try {
      const { data } = await adminApi().post("/admin/v2/seo/ping-search-engines");
      const ix = data?.indexnow || {};
      const goog = data?.google || {};
      const ixLabel = ix.skipped
        ? `IndexNow: skipped (${ix.skipped})`
        : ix.ok
          ? `IndexNow: ${ix.status || "ok"} ✓`
          : `IndexNow: ${ix.status ?? "error"}`;
      const googLabel = goog.ok
        ? `Google: ${goog.status} ✓`
        : `Google: ${goog.status ?? "error"} (deprecated by Google)`;
      const okOverall = !!ix.ok;
      (okOverall ? toast.success : toast.message)(
        "Search engines pinged",
        { description: `${ixLabel} · ${googLabel}` },
      );
      setLastPing({
        pinged_at: data?.pinged_at,
        indexnow:  ix,
        google:    goog,
        ok_overall: okOverall,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Ping failed");
    } finally {
      setPinging(false);
    }
  }

  return (
    <main
      data-testid="admin-dashboard-page"
      className="bs-admin-redesign min-h-screen bg-[#fdf8f3] px-6 py-12"
    >
      <div className="mx-auto max-w-6xl">
        <header className="mb-10 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <PortalLogo />
            <span className="ml-2 inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white px-2.5 py-0.5 text-[10px] uppercase tracking-wider text-stone-600">
              <ShieldCheck className="h-3 w-3" /> Admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-xs text-stone-500 sm:block">
              {admin?.email}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={onLogout}
              data-testid="admin-logout"
            >
              <LogOut className="mr-1.5 h-4 w-4" /> Sign out
            </Button>
          </div>
        </header>

        <div className="bs-dashboard-intro"><div>
        <h1 className="text-3xl font-semibold tracking-tight text-stone-900">
          Dashboard
        </h1>
        <p className="mt-1 text-sm text-stone-600">
          Catalog health and operational metrics, pulled live from the database.
        </p>

        </div>
        {week1 && week1.total > 0 && (
          <Link
            to="/admin/verify?week=1"
            data-testid="week1-launch-banner"
            className="mt-6 flex items-center gap-3 rounded-2xl border border-amber-300 bg-gradient-to-br from-amber-50 to-white px-5 py-4 shadow-sm transition hover:border-amber-400 hover:shadow"
          >
            <span className="rounded-xl bg-amber-100 p-2 text-amber-700">
              <CalendarRange className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-stone-900" data-testid="week1-banner-text">
                {week1.label}: {week1.verified} of {week1.total} verified
                {" · "}
                <span className="text-amber-800">launch-ready at {week1.total}</span>
              </p>
              <p className="mt-0.5 text-xs text-stone-500">
                Week 1 of the verification plan — open the queue to keep going.
              </p>
            </div>
            <span className="ml-auto shrink-0 text-xs font-semibold uppercase tracking-wider text-amber-700">
              Open Week 1 →
            </span>
          </Link>
        )}

        </div>
        {error && (
          <p
            data-testid="dashboard-error"
            className="mt-6 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            Failed to load metrics: {error}
          </p>
        )}

        {/* Interactions block — biggest, most important */}
        <section className="bs-metrics-primary grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          <Stat loading={loading}
            testId="stat-interactions-total"
            icon={<Database className="h-5 w-5" />}
            label="Total interactions"
            value={metrics?.interactions?.total}
          />
          <Stat loading={loading}
            testId="stat-interactions-verified"
            icon={<BadgeCheck className="h-5 w-5 text-emerald-600" />}
            label="Verified"
            value={metrics?.interactions?.verified}
            footer={metrics ? `${metrics.interactions.pct_verified}% of total` : "—"}
          />
          <Stat loading={loading}
            testId="stat-interactions-unverified"
            icon={<AlertTriangle className="h-5 w-5 text-amber-600" />}
            label="Unverified queue"
            value={metrics?.interactions?.unverified}
            footer="needs verification"
          />
          <Stat loading={loading}
            testId="stat-error-reports"
            icon={<FileWarning className="h-5 w-5 text-red-600" />}
            label="Open error reports"
            value={metrics?.error_reports?.open}
          />
        </section>

        <section className="bs-metrics-secondary mt-3 grid gap-3 md:grid-cols-3">
          <Stat loading={loading}
            testId="stat-products"
            icon={<Boxes className="h-5 w-5" />}
            label="Products"
            value={metrics?.products?.total}
          />
          <Stat loading={loading}
            testId="stat-users"
            icon={<Users className="h-5 w-5" />}
            label="Total users"
            value={metrics?.users?.total}
          />
          <Stat loading={loading}
            testId="stat-premium"
            icon={<BadgeCheck className="h-5 w-5 text-emerald-600" />}
            label="Premium subscribers"
            value={metrics?.users?.premium}
          />
        </section>

        {/* Quick actions */}
        <section className="bs-quick-actions">
          <h2 className="text-xs uppercase tracking-wider text-stone-500">
            Quick actions
          </h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <ActionCard
              testId="qa-import"
              to="/admin/import"
              icon={<Upload className="h-5 w-5" />}
              title="Bulk Import"
              subtitle="Paste AI-generated interactions, parse and create rows."
            />
            <ActionCard
              testId="qa-verify"
              to="/admin/verify"
              icon={<BadgeCheck className="h-5 w-5" />}
              title="Verification Queue"
              subtitle={
                metrics
                  ? `${metrics.interactions.unverified} unverified`
                  : "Verify pending interactions"
              }
            />
            <ActionCard
              testId="qa-products"
              to="/admin/products"
              icon={<Boxes className="h-5 w-5" />}
              title="Product Manager"
              subtitle="Add, edit and bulk-import products."
            />
            <ActionCard
              testId="qa-catalog-v2"
              to="/admin/catalog"
              icon={<Database className="h-5 w-5" />}
              title="Medication catalogue"
              subtitle="Edit medications, ingredients, products and interactions: names, brands, active flag, DailyMed label, has-course toggle."
            />
            <ActionCard
              testId="qa-errors"
              to="/admin/errors"
              icon={<FileWarning className="h-5 w-5" />}
              title="Error Reports"
              subtitle={
                metrics
                  ? `${metrics.error_reports.open} open`
                  : "Triage user-reported issues"
              }
            />
            <ActionCard
              testId="qa-ingredient-submissions"
              to="/admin/ingredient-submissions"
              icon={<ClipboardList className="h-5 w-5" />}
              title="Ingredient Submissions"
              subtitle={
                metrics?.ingredient_submissions?.pending != null
                  ? `${metrics.ingredient_submissions.pending} pending review`
                  : "Review user-pasted INCI lists for partial-data products."
              }
            />
            <ActionCard
              testId="qa-barcode-submissions"
              to="/admin/barcode-submissions"
              icon={<ScanLine className="h-5 w-5" />}
              title="Barcode Submissions"
              subtitle={
                metrics?.barcode_submissions?.pending != null
                  ? `${metrics.barcode_submissions.pending} pending review`
                  : "Approve user-submitted UPC/EAN codes for unscannable products."
              }
            />
            <ActionCard
              testId="qa-photo-submissions"
              to="/admin/photo-submissions"
              icon={<Camera className="h-5 w-5" />}
              title="Photo Submissions"
              subtitle={
                metrics?.photo_submissions?.pending != null
                  ? `${metrics.photo_submissions.pending} pending review`
                  : "Approve community-uploaded product photos for missing imagery."
              }
            />
            <ActionCard
              testId="qa-report-templates"
              to="/admin/report-templates"
              icon={<MessageSquareQuote className="h-5 w-5" />}
              title="Report Templates"
              subtitle="Prescriber questions shown in the Discussion Report — edit and approve them by severity."
            />
            <ActionCard
              testId="qa-alternatives"
              to="/admin/alternatives"
              icon={<PackageCheck className="h-5 w-5" />}
              title="What to use instead"
              subtitle="Manage the 'what to use instead' products shown on RED/CAUTION checks and in the report."
            />
            <ActionCard
              testId="qa-courses"
              to="/admin/courses"
              icon={<CalendarRange className="h-5 w-5" />}
              title="Course Tracker"
              subtitle="Write week-by-week treatment timelines; users track their start date and get weekly nudges."
            />
            <ActionCard
              testId="qa-med-guidance"
              to="/admin/medication-guidance"
              icon={<Stethoscope className="h-5 w-5" />}
              title="Medication notes (watch-for & restart)"
              subtitle="Feeds page 2 of the Discussion Report."
            />
            <ActionCard
              testId="qa-data-quality"
              to="/admin/data-quality"
              icon={<Languages className="h-5 w-5" />}
              title="Data Quality"
              subtitle={
                metrics?.data_quality?.non_english != null
                  ? `${metrics.data_quality.non_english} products with non-English ingredients — one-click Claude translate.`
                  : "Translate non-English ingredient lists into standard English INCI."
              }
            />
            <ActionButton
              testId="qa-ping-search-engines"
              onClick={onPingSearchEngines}
              busy={pinging}
              icon={<Radio className="h-5 w-5" />}
              title="Ping search engines"
              subtitle={
                lastPing?.pinged_at
                  ? `Last pinged ${relativeTime(lastPing.pinged_at)}${
                      lastPing.ok_overall ? " ✓" : ""
                    }`
                  : "Nudge IndexNow (Bing/Yandex) + Google after a catalog update."
              }
            />
            <ActionCard
              testId="qa-gsc-setup"
              to="/admin/seo-setup"
              icon={<Search className="h-5 w-5" />}
              title="Search Engine Setup"
              subtitle={
                gscStatus
                  ? (gscStatus.gsc_verification_set
                      ? `GSC verified ✓ · IndexNow ${gscStatus.indexnow_key_set ? "active ✓" : "off"}`
                      : "Action needed — GSC not verified")
                  : "Verify GSC + submit sitemap"
              }
            />
            <ActionCard
              testId="qa-pair-content"
              to="/admin/pair-content"
              icon={<Sparkles className="h-5 w-5" />}
              title="Pair Content Generator"
              subtitle={
                pairStatus
                  ? (pairStatus.remaining === 0
                      ? `All ${pairStatus.total} top pairs authored ✓`
                      : `${pairStatus.generated}/${pairStatus.total} authored · ${pairStatus.remaining} pending`)
                  : "Claude-authored expert copy for top 50 pairs"
              }
            />
            <ActionCard
              testId="qa-error-log"
              to="/admin/error-log"
              icon={<Bug className="h-5 w-5" />}
              title="Backend errors (24h)"
              subtitle={
                errorLog
                  ? (errorLog.total === 0
                      ? "All quiet — 0 events in last 24h ✓"
                      : `${errorLog.exceptions} exception${errorLog.exceptions === 1 ? "" : "s"} · ${errorLog.slow_queries} slow quer${errorLog.slow_queries === 1 ? "y" : "ies"}`)
                  : "Unhandled exceptions + slow Mongo queries"
              }
            />
            <ActionCard
              testId="qa-events"
              to="/admin/events"
              icon={<BarChart3 className="h-5 w-5" />}
              title="Analytics Events"
              subtitle="Conversion signals by event × day."
            />
            <ActionCard
              testId="qa-waitlist"
              to="/admin/waitlist"
              icon={<Sparkles className="h-5 w-5" />}
              title="Early-Access Waitlist"
              subtitle="Signups, country + source breakdown, recent activity."
            />
            <ActionCard
              testId="qa-contacts"
              to="/admin/contacts"
              icon={<Users className="h-5 w-5" />}
              title="Contacts"
              subtitle="Every captured email — waitlist, newsletter, sign-in, gap-notify, saved checks — in one place."
            />
            <ActionCard
              testId="qa-content-plan"
              to="/admin/content-plan"
              icon={<TrendingUp className="h-5 w-5" />}
              title="SEO Content Opportunities"
              subtitle="Medication interactions, customer questions and Search Console opportunities ranked by demand and relevance. Review and select articles to write."
            />
            <ActionCard
              testId="qa-blog"
              to="/admin/blog"
              icon={<BookOpen className="h-5 w-5" />}
              title="Blog (auto-generated)"
              subtitle="AI-written SEO articles · publishes ~3/week · generate now + kill switch."
            />
            <ActionCard
              testId="qa-maintenance"
              to="/admin/maintenance"
              icon={<Wrench className="h-5 w-5" />}
              title="Maintenance"
              subtitle="One-click admin operations: recompute scores, metrics refresh (paid), monthly toggle, GSC sync."
            />
            <ActionCard testId="qa-pharmacists" to="/admin/pharmacists" badge="NEW"
              icon={<Users className="h-5 w-5" />} title="Pharmacist management"
              subtitle="Accounts, individual assignments, review progress and history." />
          </div>
        </section>

        <FoundingFunnelWidget />
        <MedicationRequestsWidget />
        <CoverageBacklogWidget />
      </div>
    </main>
  );
}

function Stat({ testId, icon, label, value, footer, loading }) {
  return <div data-testid={testId} className="bs-metric">
    <span className="bs-metric-icon">{icon}</span>
    <div><span className="bs-metric-label">{label}</span>
      {loading ? <Loader2 className="h-6 w-6 animate-spin" aria-label="Loading"/> : <p className="bs-metric-value">{value ?? "—"}</p>}
      {!loading && footer && <p className="bs-metric-footer">{footer}</p>}
    </div>
  </div>;
}

function ActionCard({ testId, to, icon, title, subtitle, badge }) {
  return <Link to={to} data-testid={testId} className="bs-action-card">
    <span className="bs-action-icon">{icon}</span>
    <div className="bs-action-copy"><span className="bs-action-title">{title}{badge && <span className="bs-action-badge">{badge}</span>}</span><p>{subtitle}</p></div>
    <span className="bs-action-arrow" aria-hidden="true">→</span>
  </Link>;
}

/** "12 minutes ago" / "2 hours ago" / "3 days ago" — small enough to not
 *  pull in a date-fns dependency for one place. */
function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffSec = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (diffSec < 45)         return "just now";
  if (diffSec < 90)         return "1 minute ago";
  if (diffSec < 60 * 45)    return `${Math.round(diffSec / 60)} minutes ago`;
  if (diffSec < 60 * 90)    return "1 hour ago";
  if (diffSec < 60 * 60 * 22) return `${Math.round(diffSec / 3600)} hours ago`;
  if (diffSec < 60 * 60 * 36) return "1 day ago";
  return `${Math.round(diffSec / 86400)} days ago`;
}

/** ActionCard's twin, for actions that fire a request instead of navigating. */
function ActionButton({ testId, onClick, busy, icon, title, subtitle }) {
  return <button type="button" onClick={onClick} disabled={busy} data-testid={testId} className="bs-action-card">
    <span className="bs-action-icon">{busy ? <Loader2 className="h-5 w-5 animate-spin"/> : icon}</span>
    <div className="bs-action-copy"><span className="bs-action-title">{title}</span><p>{subtitle}</p>{busy && <span role="status">Pinging…</span>}</div>
    <span className="bs-action-arrow" aria-hidden="true">→</span>
  </button>;
}
